import * as CknUi from "@ckn-technology/ui";
import { Component } from "@ckn-technology/ui";
import { startKeepalive } from "./api/keepalive.js";
import { startCatalogSignal } from "./api/catalogSignal.js";
// Present only in newer @ckn-technology/ui. The installed version (5.4.1) does not export it, so a
// plain named import was a hard type error on every build — and would have been a runtime crash on
// the first line of the bundle had the build ever been run against it for real. The lint mode is a
// development aid, not something the app should refuse to start without, so it is read off the
// module and called only if it is there.
//
// The namespace is copied into a plainly-typed local FIRST, and that is load-bearing, not style.
// Reading `CknUi.setTemplateLintMode` directly is a harmony import specifier: webpack resolves it
// against the module's real export map at build time, does not find it, and emits
//   ERROR export 'setTemplateLintMode' was not found in '@ckn-technology/ui'
// — and UiMiddleware rejects on stats.hasErrors(), so the whole client bundle failed to build and
// the server would not boot at all. Optional chaining does not help: the error is about the
// reference existing, not about calling it. Through a local, the member access is an ordinary
// runtime property read on a plain object and webpack has nothing to check it against.
// Delete this indirection once the installed @ckn-technology/ui actually exports the symbol.
const cknUi = CknUi;
const setTemplateLintMode = cknUi.setTemplateLintMode;
__webpack_public_path__ = "/client/";
/**
 * Custom-element tag → the module that registers it.
 *
 * This file used to `import` all 22 app shells statically, which put **every** screen in the
 * product into one 4.85 MB chunk served on every route: the POS terminal at the till downloaded
 * the Membership admin, the OMS and the whole product back-office in order to render a till, and
 * the customer mini-app did the same on a phone. Measured numbers and the reasoning are in
 * `context/design/frontend-performance.md` §C1.
 *
 * Each entry below is a `import()`, so webpack emits one chunk per shell and the browser fetches
 * only the shells the page it is on actually mounts. Modules shared between them — the whole
 * `apps/api/*` layer — are hoisted by webpack into a shared chunk automatically, so they are
 * downloaded once rather than duplicated per shell.
 *
 * The KEY is the tag as it appears in `src/modules/main/views/main.*.ejs`. A new screen needs an
 * entry here and a view that mounts its tag; nothing else in this file changes.
 * `test/perf-budget.test.mjs` asserts this map stays in sync with what the views mount and with
 * what `Component.register` declares, so a missing entry fails the build rather than rendering an
 * empty element at runtime.
 */
const SHELLS = {
    // Launcher / console
    "brandos-launcher": () => import("./shell/shelllauncher.js"),
    "console-app": () => import("./console/consoleapp.js"),
    // Back-office module shells
    "browse-app": () => import("./browse/browseapp.js"),
    "campaign-app": () => import("./campaign/campaignapp.js"),
    "content-app": () => import("./content/contentapp.js"),
    "customer-app": () => import("./customer/customerapp.js"),
    "delivery-app": () => import("./delivery/deliveryapp.js"),
    "home-app": () => import("./home/homeapp.js"),
    "inventory-app": () => import("./inventory/inventoryapp.js"),
    "membership-app": () => import("./membership/membershipapp.js"),
    "oms-app": () => import("./oms/omsapp.js"),
    "pms-app": () => import("./pms/pmsapp.js"),
    "product-app": () => import("./product/productapp.js"),
    "purchase-app": () => import("./purchase/purchaseapp.js"),
    "rfid-app": () => import("./rfid/rfidapp.js"),
    "rfid-tracking": () => import("./rfid/rfidtracking.js"),
    "sales-app": () => import("./sales/salesapp.js"),
    // Platform — the platform-root-only rail module. Registered like any other shell: the entry
    // decides nothing about who may open it, the route (default.ts) and the API do.
    "platform-app": () => import("./platform/platformapp.js"),
    "settings-app": () => import("./settings/settingsapp.js"),
    "supplier-app": () => import("./supplier/supplierapp.js"),
    // Staff terminals — pop-up + flagship (staffv2app) and the shared login.
    "staff-app": () => import("./staff/staffapp.js"),
    "staffv2-app": () => import("./staff/staffv2app.js"),
    "staff-login": () => import("./staff/stafflogin.js"),
    // The STOCKROOM wall display (/staff_flagship/board) — its own chunk, so the TV in the back
    // room downloads a board and not a terminal.
    "staff-board": () => import("./staff/board-page.js"),
    // Staff Flagship v2 (/staff_flagship/v2) — the redesigned terminal, beside the live one and
    // off unless STAFF_V2_ENABLED=true. See staff/flagship-v2/staffflagshipv2app.ts.
    "staff-flagship-v2": () => import("./staff/flagship-v2/staffflagshipv2app.js"),
    // Customer-facing mini-app (maison KEEPS storefront) on the imb/msk theme.
    "miniapp-app": () => import("./miniapp/miniappapp.js"),
    // Mini App v2 (/miniapp/v2) — beside the live one, off unless MINIAPP_V2_ENABLED=true. See miniapp/v2/miniappappv2.ts.
    "miniapp-v2-app": () => import("./miniapp/v2/miniappappv2.js"),
    // Member-facing Membership & Loyalty app (maison KEEPS CLUB), imb/msk theme.
    "membershipapp-app": () => import("./membershipapp/membershipappapp.js"),
    // The customer's own tax-invoice request (/etax), reached by scanning the QR under a receipt.
    // Its own chunk on purpose: this page is opened from a paper slip on shop wifi by someone who
    // has never loaded anything else from this origin, so it downloads a form and nothing else.
    "etax-request": () => import("./etax/etaxrequest.js"),
    // POS — cashier terminal, customer mirror, the branch queue board, and the post-sale /
    // read-only store tools. Ten tags, ten views, one theme (imb/pos); see each module's own
    // header for the routes.
    "pos-staff": () => import("./pos/staff-page.js"),
    // POS v2 (/pos/v2) — the redesigned terminal beside the live one, off unless POS_V2_ENABLED=true.
    // Extends the live shell, so it imports it. See pos/pos-v2/posstaffv2.ts.
    "pos-staff-v2": () => import("./pos/pos-v2/posstaffv2.js"),
    "pos-history-v2": () => import("./pos/pos-v2/posv2-history.js"),
    "pos-return-v2": () => import("./pos/pos-v2/posv2-return.js"),
    "pos-lookup-v2": () => import("./pos/pos-v2/posv2-lookup.js"),
    "pos-tax-v2": () => import("./pos/pos-v2/posv2-tax.js"),
    "pos-taxreq-v2": () => import("./pos/pos-v2/posv2-taxreq.js"),
    "pos-summary-v2": () => import("./pos/pos-v2/posv2-summary.js"),
    "pos-inventory-v2": () => import("./pos/pos-v2/posv2-inventory.js"),
    "pos-tracking-v2": () => import("./pos/pos-v2/posv2-tracking.js"),
    "pos-customer": () => import("./pos/customers-page.js"),
    // The wall display (/pos/queue) — its own chunk, so the TV downloads a board and not a till.
    "pos-queue": () => import("./pos/queue-page.js"),
    "pos-lookup": () => import("./pos/lookup-page.js"),
    "pos-return": () => import("./pos/return-page.js"),
    "pos-tax": () => import("./pos/tax-page.js"),
    "pos-taxreq": () => import("./pos/taxreq-page.js"),
    "pos-summary": () => import("./pos/summary-page.js"),
    "pos-inventory": () => import("./pos/inventory-page.js"),
    "pos-history": () => import("./pos/history-page.js"),
    "pos-tracking": () => import("./pos/tracking-page.js"),
    // Local-only bench that stands in for an ONESIAM EDC burn slip. Its ROUTE 404s off loopback,
    // so this chunk is never fetched by a deployed till — see pos/devtools-barcode.ts.
    "pos-devtools-barcode": () => import("./pos/devtools-barcode.js"),
};
/**
 * Which shells this page needs — read off the DOM the view rendered, not off `location.pathname`.
 *
 * The view IS the routing table already (`main.pos.ejs` mounts `<pos-staff>`), so reading the
 * mount node means there is no second path→shell map to keep in step with
 * `src/modules/main/controllers/default.ts`. A route added there works here with no change.
 */
function wantedShells() {
    const roots = document.querySelectorAll(".cknui");
    const tags = new Set();
    for (const root of Array.from(roots)) {
        for (const el of Array.from(root.querySelectorAll("*"))) {
            const tag = el.tagName.toLowerCase();
            if (tag in SHELLS)
                tags.add(tag);
        }
    }
    return [...tags].map((t) => SHELLS[t]);
}
// Template-DSL lint mode. Every mistake in the DSL fails SILENTLY — `@click` instead of `click()`,
// a `:for` with no `:key`, a binding missing its `{{ }}` — the element renders and the control
// simply does nothing. On a dev host we want that to throw at mount, where it is one stack trace;
// in production a thrown mount would take the page down over a cosmetic binding, so it warns.
// The static pass over the same rules is `node scripts/lint-templates.mjs` (part of `npm test`).
// Set BEFORE any shell loads, so the mode is in place for the first template built.
setTemplateLintMode?.(/^(localhost|127\.0\.0\.1|\[::1\])$/.test(globalThis.location?.hostname ?? "") ? "error" : "warn");
// Keep this screen's session alive for as long as somebody is working it, and end it when nobody
// is (src/apps/api/keepalive.ts). Started BEFORE the shells load rather than from inside one: the
// rule belongs to the browser tab, not to whichever app happens to be mounted in it, and a shell
// chunk that fails to load is exactly when a screen should still know its session has lapsed.
// A no-op on the mini-app and the member app, which sign in with something else entirely.
startKeepalive();
// A catalogue write anywhere in the tenant drops THIS tab's product caches, so an operator who has
// just set a product Active does not wait out a 5-minute memo and a 2-minute browser cache to see
// it — and neither does the till in the next room. Signal only: nothing is re-fetched here, the
// next read a screen does simply misses. Opens no socket of its own; `rt` connects on the first
// subscription and is shared with the shop-floor panes.
startCatalogSignal();
// `Component.render` resolves each tag against `Component.registry`, which a module only populates
// when it runs its `Component.register(...)` call — so every shell this page mounts has to have
// finished importing BEFORE the walk starts, or its element renders as an empty tag.
//
// `.then()` rather than top-level await: TLA needs `experiments.topLevelAwait`, and the webpack
// config lives in the bundle middleware rather than a checked-in config file, so keeping the entry
// free of TLA means this file does not depend on that setting staying on.
void Promise.all(wantedShells().map((load) => load()))
    .then(() => Component.render(".cknui"))
    .catch((e) => {
    // A chunk that fails to load leaves a blank screen with nothing in the console to explain
    // it — the one failure mode this split introduces that the old single bundle could not have.
    console.error("[brandos] shell failed to load — the screen will be blank", e);
});
//# sourceMappingURL=index.js.map